import { useState } from 'react';
import { Alert, Button, Group, NumberInput, Select, Stack, Switch, TextInput, Tooltip } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import { RUN_MODES, type RunMode } from '@zebra/core';
import { ApiRequestError } from '../../../api/client';
import { useIsAdmin } from '../../../auth/permissions';
import { parseNum } from '../../../lib/parseNum';
import { useCreateStage, useSites, useUpdateStage } from '../api';
import { RUN_MODE_DESCRIPTIONS, RUN_MODE_LABELS } from '../format';
import type { StageDto } from '../types';

/** Create-or-edit form for a process stage (BUILD_SPEC.md §5.5; this goal's sole
 * ownership of stages/recipes config, plan.md QC fix #2). Editing a stage never
 * changes its `runMode`, `siteId`, or `code` once created (mirrors
 * `updateProcessStageSchema`'s own shape — a run-mode switch could misinterpret a
 * run already pinned to that stage) — PATCH only touches name/sortOrder/isActive. */
export function StageForm({ stage, onSaved }: { stage?: StageDto; onSaved: () => void }) {
  const isAdmin = useIsAdmin();
  const sitesQuery = useSites();
  const createStage = useCreateStage();
  const updateStage = useUpdateStage(stage?.id ?? '');

  const [siteId, setSiteId] = useState<string | null>(stage?.siteId ?? null);
  const [code, setCode] = useState(stage?.code ?? '');
  const [name, setName] = useState(stage?.name ?? '');
  const [runMode, setRunMode] = useState<RunMode>(stage?.runMode ?? 'SHIFT');
  const [sortOrder, setSortOrder] = useState<number | string>(stage?.sortOrder ?? 0);
  const [isActive, setIsActive] = useState(stage?.isActive ?? true);

  const isEdit = Boolean(stage);
  const mutation = isEdit ? updateStage : createStage;
  const parsedSortOrder = parseNum(sortOrder) ?? 0;

  function handleSubmit() {
    if (isEdit) {
      updateStage.mutate({ name: name.trim(), sortOrder: parsedSortOrder, isActive }, { onSuccess: onSaved });
      return;
    }
    if (!siteId || !code.trim() || !name.trim()) return;
    createStage.mutate(
      { siteId, code: code.trim(), name: name.trim(), runMode, sortOrder: parsedSortOrder },
      { onSuccess: onSaved },
    );
  }

  return (
    <Stack gap="sm">
      {!isEdit && (
        <Select
          label="Site"
          data={(sitesQuery.data?.items ?? []).map((s) => ({ value: s.id, label: `${s.code} — ${s.name}` }))}
          value={siteId}
          onChange={setSiteId}
          searchable
        />
      )}
      {!isEdit && (
        <TextInput
          label="Code"
          description="A short code for this stage, e.g. DRYER, HAMMER1, PM1"
          value={code}
          onChange={(e) => setCode(e.currentTarget.value)}
        />
      )}
      <TextInput label="Name" value={name} onChange={(e) => setName(e.currentTarget.value)} />
      {!isEdit && (
        <Select
          label="Run mode"
          description={RUN_MODE_DESCRIPTIONS[runMode]}
          data={RUN_MODES.map((m) => ({ value: m, label: RUN_MODE_LABELS[m] }))}
          value={runMode}
          onChange={(v) => setRunMode((v as RunMode) ?? 'SHIFT')}
        />
      )}
      <NumberInput
        label="Sort order"
        decimalScale={0}
        value={sortOrder}
        onChange={setSortOrder}
      />
      {isEdit && <Switch label="Active" checked={isActive} onChange={(e) => setIsActive(e.currentTarget.checked)} />}
      {mutation.isError && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {mutation.error instanceof ApiRequestError ? mutation.error.message : 'Failed to save stage'}
        </Alert>
      )}
      <Group justify="flex-end">
        <Tooltip label="Requires Admin role" disabled={isAdmin}>
          <span>
            <Button
              loading={mutation.isPending}
              disabled={!isAdmin || (!isEdit && (!siteId || !code.trim() || !name.trim()))}
              onClick={handleSubmit}
            >
              {isEdit ? 'Save changes' : 'Create stage'}
            </Button>
          </span>
        </Tooltip>
      </Group>
    </Stack>
  );
}
